<template>
  <div class="ver-detail">
    <p class="ver-detail__status" :class="'ver-detail__status--' + statusKind">
      <app-icon :name="statusIcon" :size="16" :class="{ 'ver-detail__spin': statusKind === 'running' }" />
      {{ statusText }}
    </p>

    <section class="ver-detail__section">
      <h3 class="ver-detail__heading">Version</h3>
      <dl class="ver-detail__list">
        <template v-for="row in rows">
          <dt :key="'t-' + row.label" class="ver-detail__label">{{ row.label }}</dt>
          <dd :key="'d-' + row.label" class="ver-detail__value">{{ row.value }}</dd>
        </template>
      </dl>
    </section>

    <section class="ver-detail__section">
      <h3 class="ver-detail__heading">Downloads</h3>
      <div v-if="succeeded" class="ver-detail__downloads">
        <app-button
          v-for="item in downloads"
          :key="item.key"
          icon="download"
          @click="$emit('download', item.key)"
        >{{ item.text }}</app-button>
      </div>
      <p v-else class="ver-detail__hint">Downloads stehen nach einem erfolgreichen Import bereit.</p>
    </section>

    <section class="ver-detail__section">
      <h3 class="ver-detail__heading">Import-Log</h3>
      <VersionImportLogDataGrid :version_id="version.version_id" bare />
    </section>
  </div>
</template>

<script>
// Inhalt des Detailbereichs „Fahrplanversion“ (Kundenwunsch 13.10.2026: „statt inline das aufklappen dann bitte rechts
// daneben eine zusätzliche spalte so wie die detailansicht der verkäufe“, „dann detailansicht mit zusatzinfos, downloads
// und logs“): oben der Importstatus wie in der Tabelle, darunter Abschnitte wie im Verkaufsdetail – „Version“ (ID,
// gültig ab, Stage, erstellt und geändert mit Benutzer), „Downloads“ (je Format ein Knopf, nur nach erfolgreichem
// Import; Ereignis download (key)) und „Import-Log“ (VersionImportLogDataGrid, kompakt in Festbreitenschrift).
import moment from "moment";
import { LEER } from "@/utils/format";
import VersionImportLogDataGrid from "@/components/VersionImportLogDataGrid";

const STATUS = {
  RUNNING: { kind: "running", icon: "refresh", text: "Import läuft" },
  SUCCESSFULL: { kind: "done", icon: "check", text: "Import erfolgreich" },
  FAILED: { kind: "failed", icon: "alertTriangle", text: "Import fehlerhaft" }
};

function present(value) {
  return value !== null && value !== undefined && value !== "";
}

function dateText(value, format) {
  const m = present(value) ? moment(value) : null;
  return m && m.isValid() ? m.format(format) : null;
}

// „01.10.2026, 10:00 · mhinkel“
function whenWho(time, user) {
  const parts = [dateText(time, "DD.MM.YYYY, HH:mm"), present(user) ? String(user) : null].filter(Boolean);
  return parts.length ? parts.join(" · ") : LEER;
}

export default {
  name: "VersionDetail",
  components: { VersionImportLogDataGrid },
  props: {
    version: { type: Object, required: true },
    // verfügbare Formate [{ key, text }]
    downloads: { type: Array, default: () => [] }
  },
  computed: {
    status() {
      return STATUS[this.version.import_status] || { kind: "other", icon: "info", text: this.version.import_status || LEER };
    },
    statusKind() {
      return this.status.kind;
    },
    statusIcon() {
      return this.status.icon;
    },
    statusText() {
      return this.status.text;
    },
    succeeded() {
      return this.version.import_status === "SUCCESSFULL";
    },
    rows() {
      const v = this.version;
      return [
        { label: "Versions-ID", value: v.version_id < 0 ? "?" : present(v.version_id) ? String(v.version_id) : LEER },
        { label: "Gültig ab", value: dateText(v.valid_from, "DD.MM.YYYY") || LEER },
        { label: "Stage", value: present(v.stage) ? String(v.stage) : LEER },
        { label: "Erstellt", value: whenWho(v.creation_time, v.creation_username) },
        { label: "Geändert", value: whenWho(v.modification_time, v.modification_username) }
      ];
    }
  }
};
</script>

<style scoped>
.ver-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
p.ver-detail__status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ink);
}
.ver-detail__status--done .app-icon {
  color: var(--green-dot);
}
.ver-detail__status--running {
  color: var(--amber);
}
.ver-detail__status--failed {
  color: var(--red);
}
.ver-detail__spin {
  animation: ver-detail-spin 2s infinite linear;
}
@keyframes ver-detail-spin {
  to {
    transform: rotate(359deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .ver-detail__spin {
    animation: none;
  }
}
/* Abschnitte wie im Verkaufsdetail: Linie oben, Überschrift 12/16 600 in Großbuchstaben, darunter der Inhalt */
.ver-detail__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
h3.ver-detail__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.ver-detail__list {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  gap: 8px 12px;
  margin: 0;
}
.ver-detail__label {
  font-size: 12px;
  line-height: 20px;
  color: var(--ink-3);
}
.ver-detail__value {
  margin: 0;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ver-detail__downloads {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
p.ver-detail__hint {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
</style>
